/**
 * The audio engine (Phase 8, ADR-0004): Web Audio directly. The context is created inside the
 * first pointer or key gesture and never before, so no browser logs an autoplay warning; cues
 * requested earlier are dropped. Buses sfx / music / ui feed a master gain and a compressor
 * used as a safety limiter. Positional cues go through the voice pool's panners, posed against
 * the listener the chase camera sets each frame.
 */
import { getAudioCue, type AudioBus, type AudioCue } from '@steelwake/shared/data';

import { dbToGain, levelGain, mayRetrigger, pickVariant, playbackRate } from './bank-logic.js';
import { AudioLibrary } from './library.js';
import { MusicPlayer } from './music.js';
import { VoicePool, type Vec3Like } from './voices.js';

export interface AudioLevels {
  master: number;
  sfx: number;
  music: number;
  ui: number;
}

export const DEFAULT_LEVELS: AudioLevels = { master: 0.8, sfx: 1, music: 0.6, ui: 0.8 };

export interface ListenerPose {
  position: Vec3Like;
  forward: Vec3Like;
  up: Vec3Like;
}

export interface PlayRequest {
  position?: Vec3Like | null;
  loopKey?: string;
  /** Extra trim for this play, dB. */
  gainDb?: number;
}

export interface AudioStats {
  /** 'none' until the first gesture creates the context. */
  state: 'none' | AudioContextState;
  unlocked: boolean;
  buffers: number;
  cuesPlayed: number;
  /** The last eight cue ids that sounded, oldest first. */
  lastCues: string[];
  voices: number;
  loops: number;
  music: string | null;
  captions: number;
}

interface Buses {
  master: GainNode;
  limiter: DynamicsCompressorNode;
  sfx: GainNode;
  music: GainNode;
  ui: GainNode;
}

const LAST_CUES = 8;

/** WebKit builds without the unprefixed name still carry the prefixed one. */
function audioContextCtor(): (new () => AudioContext) | null {
  const g = globalThis as {
    AudioContext?: new () => AudioContext;
    webkitAudioContext?: new () => AudioContext;
  };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

export class AudioEngine {
  readonly library: AudioLibrary;
  private ctx: AudioContext | null = null;
  private buses: Buses | null = null;
  private voices: VoicePool | null = null;
  private music: MusicPlayer | null = null;
  private levels: AudioLevels = DEFAULT_LEVELS;
  private unlocked = false;
  private cuesPlayed = 0;
  private readonly lastCues: string[] = [];
  private readonly lastPlayedAt = new Map<string, number>();
  private readonly lastVariant = new Map<string, number>();
  private captionCount = 0;
  private readonly unlockWaiters: (() => void)[] = [];
  private removeUnlock: (() => void) | null = null;
  /** Called with a cue's caption when captions are on (the HUD shows it and reads it aloud). */
  onCaption: ((text: string) => void) | null = null;
  captions = false;
  random: () => number = Math.random;

  constructor() {
    this.library = new AudioLibrary(() => this.ctx);
  }

  /** Listens for the first gesture on `target`; returns the remover. Idempotent. */
  installUnlock(target: EventTarget): () => void {
    if (this.removeUnlock) return this.removeUnlock;
    const handler = (): void => {
      this.unlock();
      if (this.ctx?.state === 'running') this.removeUnlock?.();
    };
    target.addEventListener('pointerdown', handler, { capture: true });
    target.addEventListener('keydown', handler, { capture: true });
    this.removeUnlock = () => {
      target.removeEventListener('pointerdown', handler, { capture: true });
      target.removeEventListener('keydown', handler, { capture: true });
      this.removeUnlock = null;
    };
    return this.removeUnlock;
  }

  /** Creates the context (call from a gesture handler only) and resumes it. */
  unlock(): void {
    if (!this.ctx) {
      const Ctor = audioContextCtor();
      if (!Ctor) return;
      const ctx = new Ctor();
      this.ctx = ctx;
      const master = ctx.createGain();
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.knee.value = 4;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.15;
      const sfx = ctx.createGain();
      const music = ctx.createGain();
      const ui = ctx.createGain();
      sfx.connect(master);
      ui.connect(master);
      master.connect(limiter).connect(ctx.destination);
      this.buses = { master, limiter, sfx, music, ui };
      this.voices = new VoicePool(ctx);
      this.music = new MusicPlayer(ctx, music);
      this.music.connect(master);
      this.applyLevels();
      this.unlocked = true;
      // Files fetched before any context existed (a browser without an offline context).
      void this.library.decodeDeferred();
      for (const waiter of this.unlockWaiters.splice(0)) waiter();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** Resolves once the context exists (after the first gesture). */
  whenUnlocked(): Promise<void> {
    if (this.unlocked) return Promise.resolve();
    return new Promise((resolve) => this.unlockWaiters.push(resolve));
  }

  get state(): AudioStats['state'] {
    return this.ctx?.state ?? 'none';
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  setLevels(levels: AudioLevels): void {
    this.levels = levels;
    this.applyLevels();
  }

  /**
   * Plays a cue. Captions are emitted whether or not sound can play (they serve players who
   * cannot hear it); the sound itself needs the unlock, a loaded file and a free voice.
   */
  play(cueId: string, request: PlayRequest = {}): boolean {
    const cue = getAudioCue(cueId);
    if (cue.caption !== undefined && this.captions && this.onCaption) {
      this.captionCount++;
      this.onCaption(cue.caption);
    }
    const ctx = this.ctx;
    const voices = this.voices;
    const buses = this.buses;
    if (!ctx || !voices || !buses || ctx.state !== 'running') return false;
    const now = ctx.currentTime;
    const loopKey = request.loopKey ?? null;
    if (loopKey === null && !mayRetrigger(this.lastPlayedAt.get(cue.id), now, cue.retrigger)) {
      return false;
    }
    const ready = this.library.ready(cue);
    if (ready.length === 0) return false;
    const variant = pickVariant(ready.length, this.lastVariant.get(cue.id) ?? -1, this.random);
    const path = ready[variant] ?? ready[0];
    if (path === undefined) return false;
    const buffer = this.library.get(path);
    if (!buffer) return false;
    const meta = this.library.meta(path);
    const gainDb = (meta?.gainDb ?? 0) + cue.mix + (request.gainDb ?? 0);
    const voice = voices.play(cue, buffer, buses[cue.bus], {
      position: cue.positional ? (request.position ?? null) : null,
      loopKey,
      rate: playbackRate(cue.pitch, cue.pitchVariance, this.random),
      gain: dbToGain(gainDb),
      loopStart: meta?.loopStart ?? 0,
      loopEnd: meta?.loopEnd ?? buffer.duration,
    });
    if (!voice) return false;
    if (voice.startedAt === now) {
      // A fresh voice, not a refreshed loop.
      this.lastVariant.set(cue.id, variant);
      this.lastPlayedAt.set(cue.id, now);
      this.cuesPlayed++;
      this.lastCues.push(cue.id);
      if (this.lastCues.length > LAST_CUES) this.lastCues.shift();
    }
    return true;
  }

  stopLoop(key: string): void {
    if (this.ctx && this.voices) this.voices.stopLoop(key, this.ctx.currentTime);
  }

  stopAllVoices(): void {
    this.voices?.stopAll();
  }

  /** Starts a music cue (crossfading from the current one) once its file is loaded. */
  playMusic(cueId: string): boolean {
    const cue = getAudioCue(cueId);
    if (!this.music || cue.bus !== 'music') return false;
    const path = this.library.ready(cue)[0];
    const buffer = path === undefined ? undefined : this.library.get(path);
    if (path === undefined || !buffer) return false;
    const meta = this.library.meta(path);
    this.music.play(cueId, buffer, meta, dbToGain((meta?.gainDb ?? 0) + cue.mix));
    return true;
  }

  stopMusic(seconds?: number): void {
    this.music?.stop(seconds);
  }

  duck(amount: number, seconds: number): void {
    this.music?.duck(amount, seconds);
  }

  setListener(pose: ListenerPose): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const l = ctx.listener;
    const now = ctx.currentTime;
    if ('positionX' in l && l.positionX instanceof AudioParam) {
      l.positionX.setTargetAtTime(pose.position.x, now, 0.02);
      l.positionY.setTargetAtTime(pose.position.y, now, 0.02);
      l.positionZ.setTargetAtTime(pose.position.z, now, 0.02);
      l.forwardX.setTargetAtTime(pose.forward.x, now, 0.02);
      l.forwardY.setTargetAtTime(pose.forward.y, now, 0.02);
      l.forwardZ.setTargetAtTime(pose.forward.z, now, 0.02);
      l.upX.setTargetAtTime(pose.up.x, now, 0.02);
      l.upY.setTargetAtTime(pose.up.y, now, 0.02);
      l.upZ.setTargetAtTime(pose.up.z, now, 0.02);
    } else {
      l.setPosition(pose.position.x, pose.position.y, pose.position.z);
      l.setOrientation(
        pose.forward.x,
        pose.forward.y,
        pose.forward.z,
        pose.up.x,
        pose.up.y,
        pose.up.z,
      );
    }
  }

  /** Once per rendered frame: ends stale loops, frees finished voices and faded music. */
  update(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    this.voices?.update(now);
    this.music?.update(now);
  }

  stats(): AudioStats {
    return {
      state: this.state,
      unlocked: this.unlocked,
      buffers: this.library.loaded,
      cuesPlayed: this.cuesPlayed,
      lastCues: [...this.lastCues],
      voices: this.voices?.count() ?? 0,
      loops: this.voices?.loops() ?? 0,
      music: this.music?.playing ?? null,
      captions: this.captionCount,
    };
  }

  private applyLevels(): void {
    const b = this.buses;
    if (!b) return;
    b.master.gain.value = levelGain(this.levels.master);
    const bus = (name: AudioBus): GainNode => b[name];
    bus('sfx').gain.value = levelGain(this.levels.sfx);
    bus('music').gain.value = levelGain(this.levels.music);
    bus('ui').gain.value = levelGain(this.levels.ui);
  }
}

export type { AudioCue };
